import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Vault, AlertTriangle, CalendarDays, CheckCircle2, ShieldCheck } from "lucide-react";
import { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { money, fmtDate } from "../../lib/format";
import { Stat, StatusBadge, Loading, ProgressBar, Money } from "../../components/common";

const BANNER = {
  ON_TRACK: ["bg-emerald-50 border-emerald-300 text-emerald-800", "YOU'RE ON TRACK.", CheckCircle2],
  SHORTFALL: ["bg-amber-50 border-amber-300 text-amber-800", "TAX VAULT SHORTFALL — reserves are below your current obligation.", AlertTriangle],
  PAST_DUE: ["bg-rose-50 border-rose-300 text-rose-800", "PAST DUE — immediate attention required.", AlertTriangle],
  READY_TO_FILE: ["bg-sky-50 border-sky-300 text-sky-800", "READY TO FILE — your return awaits review or authorization.", FileCheckIcon],
};
function FileCheckIcon(props) { return <CheckCircle2 {...props} />; }

export default function Dashboard() {
  const { business } = useAuth();
  const [data, setData] = useState(null);
  const [noBusiness, setNoBusiness] = useState(false);
  const [chart, setChart] = useState([]);
  const [score, setScore] = useState(null);

  useEffect(() => {
    API.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setNoBusiness(true));
    API.get("/reports/sales-by-month").then((r) => setChart(r.data.chart.slice(-8))).catch(() => {});
    API.get("/compliance-score").then((r) => setScore(r.data)).catch(() => {});
  }, []);

  if (noBusiness) return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-center" data-testid="no-business-state">
      <h2 className="text-xl font-bold text-navy">No business account linked</h2>
      <p className="max-w-md text-sm text-slate-500">This staff account isn't linked to a customer business. Open a customer from the admin area, or use Impersonate on a customer record to view their portal.</p>
      <Link to="/admin/customers" className="btn-navy" data-testid="goto-customers">Go to Customers</Link>
    </div>
  );
  if (!data) return <Loading label="Loading your dashboard" />;
  const [bannerCls, bannerText, BannerIcon] = BANNER[data.status_overall] || BANNER.ON_TRACK;
  const ob = data.business.onboarding || {};
  const obItems = [
    ["Business Information", ob.business_info], ["Tax Information", ob.tax_info],
    ["Merchant Account", ob.merchant_connected, "Connected", "Action Required"],
    ["Bank Account", ob.bank_connected, "Connected", "Action Required"],
    ["Previous Returns", ob.previous_returns, "Uploaded", "Needed"],
    ["Tax Professional Review", ob.pro_review, "Complete", "Pending"],
  ];

  return (
    <div data-testid="customer-dashboard">
      <div className={`mb-6 flex items-center gap-3 rounded-md border px-4 py-3 font-semibold ${bannerCls}`} data-testid="status-banner">
        <BannerIcon className="h-5 w-5" /> STATUS: {bannerText}
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-testid="welcome-heading">
          Welcome, {data.business.dba || data.business.legal_name}
        </h1>
        <p className="text-sm text-slate-500">{data.current_period?.label} · {data.business.case_number}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Total Sales (YTD)" cents={data.total_sales_cents} tone="navy" icon={TrendingUp} testid="stat-total-sales" />
        <Stat label="Taxable Sales (YTD)" cents={data.taxable_sales_cents} tone="blue" testid="stat-taxable-sales" />
        <Stat label="Non-Taxable (YTD)" cents={data.non_taxable_sales_cents} tone="navy" testid="stat-nontaxable-sales" />
        <Stat label="Est. Tax Owed (YTD)" cents={data.estimated_tax_cents} tone="gold" testid="stat-estimated-tax" />
        <Stat label="Tax Reserved" cents={data.tax_reserved_cents} tone="green" icon={Vault} testid="stat-tax-reserved" />
        <Stat label="Tax Shortfall" cents={data.tax_shortfall_cents} tone={data.tax_shortfall_cents > 0 ? "red" : "green"} testid="stat-tax-shortfall" />
        <Stat label="Past-Due Tax" cents={data.past_due_cents} tone={data.past_due_cents > 0 ? "red" : "green"} testid="stat-past-due" />
        <Stat label="Available Revenue" cents={data.available_revenue_cents} tone="navy" testid="stat-available-revenue" />
        <Stat label="Safe To Spend" cents={data.safe_to_spend?.safe_to_spend_cents} tone="green" icon={ShieldCheck}
          sub="After all reserves" testid="stat-safe-to-spend" />
        <Stat label="Next Filing" value={data.next_filing ? fmtDate(data.next_filing.due_date) : "—"} tone="yellow"
          icon={CalendarDays} sub={data.next_filing?.label} testid="stat-next-filing" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="card-gov p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold text-navy">Sales & Tax Liability Trend</h3>
            <Link to="/portal/reports" className="text-sm font-semibold text-copper hover:underline" data-testid="view-reports-link">All reports →</Link>
          </div>
          <div className="h-64" data-testid="sales-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart.map((c) => ({ name: c.label, Sales: c.gross / 100, Tax: c.liability / 100 }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Area type="monotone" dataKey="Sales" stroke="#1B3A5C" fill="#1B3A5C" fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="Tax" stroke="#B23B23" fill="#B23B23" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card-gov border-t-4 border-t-gold p-5" data-testid="vault-mini-card">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-navy">Tax Vault</h3>
              <StatusBadge status={data.vault_funded_pct >= 100 ? "FUNDED" : "SHORTFALL"} />
            </div>
            <div className="money mt-3 text-3xl font-bold text-navy">{data.vault_funded_pct}% <span className="text-sm font-medium text-slate-400">funded</span></div>
            <div className="mt-3"><ProgressBar pct={data.vault_funded_pct} tone={data.vault_funded_pct >= 100 ? "green" : data.vault_funded_pct >= 80 ? "yellow" : "red"} /></div>
            <div className="mt-3 flex justify-between text-sm">
              <span className="text-slate-500">Reserved <Money cents={data.tax_reserved_cents} className="font-semibold text-slate-700" /></span>
              <span className="text-slate-500">Outstanding <Money cents={data.vault_required_cents ?? data.estimated_tax_cents} className="font-semibold text-slate-700" /></span>
            </div>
            <Link to="/portal/vault" className="btn-outline-gov mt-4 w-full" data-testid="goto-vault">Open Tax Vault</Link>
          </div>

          {score && (
            <div className="card-gov p-5" data-testid="compliance-score-card">
              <h3 className="font-bold text-navy">Compliance Score</h3>
              <div className="mt-2 flex items-end gap-1">
                <span className={`money text-4xl font-bold ${score.score >= 90 ? "text-emerald-600" : score.score >= 70 ? "text-amber-600" : "text-rose-600"}`} data-testid="compliance-score">{score.score}</span>
                <span className="text-slate-400">/100</span>
              </div>
              <ul className="mt-3 space-y-1.5">
                {score.factors.map((fac) => (
                  <li key={fac.factor} className="flex justify-between text-xs">
                    <span className="text-slate-600">{fac.factor}</span>
                    <span className={fac.impact < 0 ? "font-semibold text-rose-600" : "font-semibold text-emerald-600"}>{fac.impact === 0 ? "OK" : fac.impact}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[10px] text-slate-400">{score.disclaimer}</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="card-gov p-5" data-testid="upcoming-payments-card">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-bold text-navy">Upcoming Payments</h3>
            <Link to="/portal/payments" className="text-sm font-semibold text-copper hover:underline">Payment center →</Link>
          </div>
          {data.upcoming_payments.length === 0 ? (
            <p className="text-sm text-slate-500">No upcoming payments scheduled.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.upcoming_payments.slice(0, 5).map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2.5 text-sm">
                  <div>
                    <div className="font-semibold text-slate-700">{p.tax_type} · {p.case_number}</div>
                    <div className="text-xs text-slate-400">Due {fmtDate(p.due_date)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Money cents={p.amount_cents} className="font-bold text-navy" />
                    <StatusBadge status={p.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {data.business.onboarding_percent < 100 && (
          <div className="card-gov border-t-4 border-t-sky-500 p-5" data-testid="onboarding-card">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-navy">AZ Tax Accounts Setup</h3>
              <span className="money text-lg font-bold text-navy" data-testid="onboarding-percent">{data.business.onboarding_percent}%</span>
            </div>
            <div className="mt-3"><ProgressBar pct={data.business.onboarding_percent} tone="blue" /></div>
            <ul className="mt-4 space-y-2">
              {obItems.map(([label, complete, okWord, needWord]) => (
                <li key={label} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{label}</span>
                  {complete
                    ? <span className="flex items-center gap-1 font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {okWord || "Complete"}</span>
                    : <span className="font-semibold text-amber-700">{needWord || "Pending"}</span>}
                </li>
              ))}
            </ul>
            <Link to="/portal/settings" className="btn-navy mt-4 w-full" data-testid="complete-setup-btn">Complete Setup</Link>
          </div>
        )}
      </div>
    </div>
  );
}
